export type AdminApiErrorKind =
    | 'authentication'
    | 'permission'
    | 'conflict'
    | 'validation'
    | 'rate_limit'
    | 'dependency'
    | 'network'
    | 'unknown';

export class AdminApiError extends Error {
    constructor(
        message: string,
        public readonly kind: AdminApiErrorKind,
        public readonly status?: number,
        public readonly code?: string,
        public readonly requestId?: string,
        public readonly retryAfterSeconds?: number,
    ) {
        super(message);
        this.name = 'AdminApiError';
    }
}

const safeMessage = (kind: AdminApiErrorKind): string => ({
    authentication: 'Your session has expired. Please sign in again.',
    permission: 'You do not have permission to perform this action.',
    conflict: 'This information changed. Refresh and try again.',
    validation: 'Review the supplied information and try again.',
    rate_limit: 'Too many requests. Wait briefly and try again.',
    dependency: 'A required service is temporarily unavailable.',
    network: 'The server could not be reached. Check your connection.',
    unknown: 'The request could not be completed.',
})[kind];

export function normalizeAdminApiError(error: unknown): AdminApiError {
    if (error instanceof AdminApiError) return error;
    const axiosError = error as { isAxiosError?: boolean; response?: { status: number; data?: unknown; headers: Record<string, string | undefined> } };
    if (!axiosError?.isAxiosError) return new AdminApiError(safeMessage('unknown'), 'unknown');
    if (!axiosError.response) return new AdminApiError(safeMessage('network'), 'network');

    const status = axiosError.response.status;
    const body = axiosError.response.data as { code?: string; meta?: { requestId?: string }; requestId?: string } | undefined;
    const requestId = body?.meta?.requestId || body?.requestId || axiosError.response.headers['x-request-id'];
    const code = body?.code;
    const kind: AdminApiErrorKind = status === 401 ? 'authentication'
        : status === 403 ? 'permission'
            : status === 409 ? 'conflict'
                : status === 422 || status === 400 ? 'validation'
                    : status === 429 ? 'rate_limit'
                        : status >= 500 ? 'dependency'
                            : 'unknown';
    const retryHeader = axiosError.response.headers['retry-after'];
    const retryAfterSeconds = retryHeader && /^\d+$/.test(String(retryHeader)) ? Number(retryHeader) : undefined;
    return new AdminApiError(safeMessage(kind), kind, status, code, requestId, retryAfterSeconds);
}
